import React, { useState, useRef, useEffect } from 'react';
import { Send, Search, ArrowLeft, MessageCircle } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { MESSAGES } from '../data/mockData';
import './ChatPage.css';

// TODO(deferred): ChatPage zeigt noch Mock-Daten. Das Backend (/api/chat/*) ist fertig
// und abgesichert — Anbindung: fetchConversations/createConversation aus services/api.js
// + GET/POST /chat/conversations/:id/messages.
const CONVERSATIONS = [
  { id: 1, name: 'TechStart AG', role: 'Junior Webentwickler', lastMsg: 'Wie wäre es nächsten Dienstag...', time: '11:02', unread: 1, initials: 'TS', color: '#667eea' },
  { id: 2, name: 'Bloom Marketing', role: 'Social Media Manager', lastMsg: 'Vielen Dank für Ihre Bewerbung!', time: 'Gestern', unread: 0, initials: 'BM', color: '#f093fb' },
  { id: 3, name: 'LernHoch', role: 'Nachhilfelehrer', lastMsg: 'Wann können Sie anfangen?', time: 'Mo', unread: 2, initials: 'LH', color: '#43e97b' },
];

export default function ChatPage() {
  const { user, openAuth } = useApp();
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState(MESSAGES);
  const [input, setInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [convLoading, setConvLoading] = useState(true);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setConvLoading(false), 700);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (activeConv) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, activeConv]);

  if (!user) {
    return (
      <div className="chat-gate">
        <div className="chat-gate__content">
          <div className="chat-gate__icon"><MessageCircle size={48} strokeWidth={1.5} /></div>
          <h2>Nachrichten</h2>
          <p>Melde dich an, um direkt mit Arbeitgebern zu chatten.</p>
          <button className="chat-gate__btn" onClick={() => openAuth('login')}>Anmelden</button>
        </div>
      </div>
    );
  }

  const send = (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    const newMsg = {
      id: Date.now(),
      from: 'Du',
      fromAvatar: 'Du',
      message: input.trim(),
      time: new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }),
      isOwn: true,
    };
    setMessages(prev => [...prev, newMsg]);
    setInput('');

    setTimeout(() => {
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        from: activeConv?.name || 'Arbeitgeber',
        fromAvatar: activeConv?.initials || 'AG',
        message: 'Danke für Ihre Nachricht! Wir melden uns in Kürze bei Ihnen.',
        time: new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }),
        isOwn: false,
      }]);
    }, 1200);
  };

  const filteredConvs = CONVERSATIONS.filter(c =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.role.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalUnread = CONVERSATIONS.reduce((s, c) => s + c.unread, 0);

  // Chat view (when a conversation is active)
  if (activeConv) {
    return (
      <div className="chat-view">
        {/* Chat header */}
        <div className="chat-view__header">
          <button className="chat-back-btn" onClick={() => setActiveConv(null)}>
            <ArrowLeft size={22} />
          </button>
          <div className="chat-view__avatar" style={{ background: activeConv.color }}>
            {activeConv.initials}
          </div>
          <div className="chat-view__info">
            <p className="chat-view__name">{activeConv.name}</p>
            <p className="chat-view__role">{activeConv.role}</p>
          </div>
          <div className="chat-online">
            <span className="chat-online__dot" />
            Online
          </div>
        </div>

        {/* Messages */}
        <div className="chat-messages">
          <div className="chat-date-divider">Heute</div>
          {messages.map(msg => (
            <div key={msg.id} className={`chat-msg${msg.isOwn ? ' chat-msg--own' : ''}`}>
              {!msg.isOwn && (
                <div className="chat-msg__avatar" style={{ background: activeConv.color }}>
                  {msg.fromAvatar}
                </div>
              )}
              <div className="chat-msg__content">
                {msg.jobTitle && (
                  <div className="chat-msg__job-ref">📌 {msg.jobTitle}</div>
                )}
                <div className="chat-msg__bubble">{msg.message}</div>
                <span className="chat-msg__time">{msg.time}</span>
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <form className="chat-input-row" onSubmit={send}>
          <input
            type="text"
            className="chat-input"
            placeholder="Nachricht schreiben..."
            value={input}
            onChange={e => setInput(e.target.value)}
          />
          <button type="submit" className="chat-send-btn" disabled={!input.trim()}>
            <Send size={18} />
          </button>
        </form>
      </div>
    );
  }

  // Conversation list view
  return (
    <div className="chat-page">
      {/* Header */}
      <div className="chat-page__header">
        <h1>Nachrichten</h1>
        {totalUnread > 0 && (
          <span className="chat-page__badge">{totalUnread}</span>
        )}
      </div>

      {/* Search */}
      <div className="chat-search-bar">
        <Search size={16} />
        <input
          type="search"
          placeholder="Suchen..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Conversation list */}
      {convLoading ? (
        <div className="chat-skel">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="chat-skel-row">
              <div className="skel chat-skel-avatar" />
              <div className="chat-skel-lines">
                <div className="skel chat-skel-line" style={{ width: `${62 - i * 6}%` }} />
                <div className="skel chat-skel-line chat-skel-line--sm" style={{ width: `${45 - i * 5}%` }} />
              </div>
            </div>
          ))}
        </div>
      ) : (
      <div className="chat-conv-list">
        {filteredConvs.length === 0 && (
          <div className="chat-conv-empty">
            <MessageCircle size={40} strokeWidth={1.5} />
            <p>Keine Gespräche gefunden.</p>
          </div>
        )}
        {filteredConvs.map(conv => (
          <button
            key={conv.id}
            className="chat-conv-item"
            onClick={() => { setActiveConv(conv); setMessages(MESSAGES); }}
          >
            <div className="chat-conv-avatar" style={{ background: conv.color }}>
              {conv.initials}
              {conv.unread > 0 && (
                <span className="chat-conv-unread">{conv.unread}</span>
              )}
            </div>
            <div className="chat-conv-info">
              <div className="chat-conv-top">
                <span className="chat-conv-name">{conv.name}</span>
                <span className="chat-conv-time">{conv.time}</span>
              </div>
              <p className="chat-conv-role">{conv.role}</p>
              <p className="chat-conv-last">{conv.lastMsg}</p>
            </div>
          </button>
        ))}
      </div>
      )}
    </div>
  );
}
